import { getApiUrls } from "../lib/apiUrl";

export type SubmissionMeta = {
  user_id: string;
  first_name?: string;
  last_name?: string;
  submission_id: string;
  district_id?: string;
  district_name?: string;
  school_id?: string;
  school_name?: string;
  grade: string;
  test_type: string;
  status: string;
  created_at: string;
  correct_answer_count: number;
  assigned_question_count: number;
  oral_reading_submission_count: number;
  oral_reading_audio_1_url?: string;
  oral_reading_audio_2_url?: string;
  oral_reading_audio_3_url?: string;
  recap_audio_url?: string;
};

export type SubmissionsResponse = {
  submissions: SubmissionMeta[];
  total?: number;
  page?: number;
  limit?: number;
};

type FetchSubmissionsParams = {
  token: string | null | undefined;
  isSuperAdmin: boolean;
  districtIds?: string[];
  schoolIds?: string[];
  testType?: string;
  page?: number;
  limit?: number;
  signal?: AbortSignal;
  assessmentMode?: string;
  benchmarkId?: string;
};

type SubmissionActionParams = {
  token: string | null | undefined;
  submissionId: string;
};

function getSubmissionsApiUrl(): string {
  const { assessmentsApiUrl } = getApiUrls();
  if (!assessmentsApiUrl) {
    throw new Error("Assessments API URL is not configured.");
  }
  return assessmentsApiUrl;
}

function requireToken(token: string | null | undefined): string {
  if (!token) {
    throw new Error("Missing auth token.");
  }
  return token;
}

function normalizeIds(ids: string[] | undefined): string[] {
  return ids?.map((id) => id.trim()).filter(Boolean) ?? [];
}

function appendIdsParam(url: URL, key: string, ids: string[] | undefined) {
  const normalizedIds = normalizeIds(ids);
  if (normalizedIds && normalizedIds.length > 0) {
    url.searchParams.set(key, normalizedIds.join(","));
  }
}

function buildSubmissionsUrl({
  isSuperAdmin,
  districtIds,
  schoolIds,
  testType,
  page,
  limit,
  assessmentMode,
  benchmarkId,
}: Omit<FetchSubmissionsParams, "token" | "signal">): URL {
  const submissionsUrl = new URL(`${getSubmissionsApiUrl()}/submissions`);

  if (isSuperAdmin) {
    submissionsUrl.searchParams.set("all", "true");
    appendIdsParam(submissionsUrl, "districtIds", districtIds);
  } else {
    const normalizedDistrictIds = normalizeIds(districtIds);
    if (normalizedDistrictIds.length !== 1) {
      throw new Error("No district is assigned to this admin.");
    }
    appendIdsParam(submissionsUrl, "districtIds", normalizedDistrictIds);
  }

  appendIdsParam(submissionsUrl, "schoolIds", schoolIds);
  if (benchmarkId) {
    submissionsUrl.searchParams.set("benchmarkId", benchmarkId);
  }
  if (testType) {
    submissionsUrl.searchParams.set("testType", testType);
  }
  if (typeof page === "number") {
    submissionsUrl.searchParams.set("page", String(page));
  }
  if (assessmentMode) {
    submissionsUrl.searchParams.set("assessmentMode", assessmentMode);
  }
  if (typeof limit === "number") {
    submissionsUrl.searchParams.set("limit", String(limit));
  }

  return submissionsUrl;
}

export async function fetchSubmissions({
  token,
  signal,
  ...urlParams
}: FetchSubmissionsParams): Promise<SubmissionsResponse> {
  const response = await fetch(buildSubmissionsUrl(urlParams).toString(), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${requireToken(token)}`,
    },
    signal,
  });

  if (!response.ok) {
    throw new Error(`Unable to load submissions (${response.status})`);
  }

  return (await response.json()) as SubmissionsResponse;
}

export async function deleteSubmission({
  token,
  submissionId,
}: SubmissionActionParams): Promise<void> {
  const normalizedSubmissionId = submissionId.trim();
  if (!normalizedSubmissionId) {
    throw new Error("Missing submission id.");
  }

  const submissionsUrl = new URL(`${getSubmissionsApiUrl()}/submissions`);
  submissionsUrl.searchParams.set("submissionId", normalizedSubmissionId);

  const response = await fetch(submissionsUrl.toString(), {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${requireToken(token)}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Unable to delete submission (${response.status})`);
  }
}

export async function resetSubmission({
  token,
  submissionId,
}: SubmissionActionParams): Promise<void> {
  const normalizedSubmissionId = submissionId.trim();
  if (!normalizedSubmissionId) {
    throw new Error("Missing submission id.");
  }

  const resetUrl = new URL(`${getSubmissionsApiUrl()}/submissions/reset`);
  resetUrl.searchParams.set("submissionId", normalizedSubmissionId);

  const response = await fetch(resetUrl.toString(), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${requireToken(token)}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Unable to reset submission (${response.status})`);
  }
}
